/**
 * Shared Chat Rendering Components
 * ChartRenderer, parseContentWithCharts, FormattedChatMessage
 * Used by ClientFileChatSidebar and Manager PA VoicePage.
 */

import { useMemo } from 'react';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  PieChart, Pie, Cell, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

const CHART_COLORS = ['#3b82f6', '#ef4444', '#22c55e', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316', '#14b8a6', '#6366f1'];

function formatChartValue(value: number) {
  if (Math.abs(value) >= 100) return `$${value.toLocaleString()}`;
  return value.toLocaleString();
}

export function ChartRenderer({ chartData }: { chartData: { type: string; title?: string; data: any[]; keys?: string[] } }) {
  const { type, title, data, keys } = chartData;
  if (!data || data.length === 0) return null;

  const normalizedType = type.toLowerCase().replace(/[_\s-]/g, '');
  const chartHeight = normalizedType === 'horizontalbar' || normalizedType === 'horizontal' || normalizedType === 'hbar' ? Math.max(250, data.length * 30) : 280;

  const renderChart = () => {
    switch (normalizedType) {
      case 'pie': case 'piechart': case 'donut': case 'donutchart':
        return (
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="name" cx="35%" cy="50%"
              outerRadius={90} innerRadius={normalizedType.includes('donut') ? 50 : 0}>
              {data.map((_: any, i: number) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
            </Pie>
            <Tooltip formatter={(v: number) => formatChartValue(v)} />
            <Legend layout="vertical" align="right" verticalAlign="middle" iconType="square" wrapperStyle={{ fontSize: '11px' }} />
          </PieChart>
        );

      case 'bar': case 'barchart':
        return (
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={data.length > 6 ? -30 : 0} textAnchor={data.length > 6 ? 'end' : 'middle'} height={data.length > 6 ? 60 : 30} />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`} />
            <Tooltip formatter={(v: number) => formatChartValue(v)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {keys ? keys.map((key, i) => <Bar key={key} dataKey={key} fill={CHART_COLORS[i % CHART_COLORS.length]} radius={[4, 4, 0, 0]} activeBar={false}/>) : <Bar dataKey="value" fill={CHART_COLORS[0]} radius={[4, 4, 0, 0]} activeBar={false}>
              {data.map((_: any, i: number) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
            </Bar>}
          </BarChart>
        );

      case 'horizontalbar': case 'horizontal': case 'hbar':
        return (
          <BarChart data={data} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`} />
            <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={120} />
            <Tooltip formatter={(v: number) => formatChartValue(v)} />
            {keys ? keys.map((key, i) => <Bar key={key} dataKey={key} fill={CHART_COLORS[i % CHART_COLORS.length]} activeBar={false}/>) : <Bar dataKey="value" fill={CHART_COLORS[0]} radius={[0, 4, 4, 0]} activeBar={false}/>}
          </BarChart>
        );

      case 'line': case 'linechart':
        return (
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey="name" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`} />
            <Tooltip formatter={(v: number) => formatChartValue(v)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {keys ? keys.map((key, i) => <Line key={key} type="monotone" dataKey={key} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} dot={{ r: 4 }} />) : <Line type="monotone" dataKey="value" stroke={CHART_COLORS[0]} strokeWidth={2} dot={{ r: 4 }} />}
          </LineChart>
        );

      default:
        return (
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey="name" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip formatter={(v: number) => formatChartValue(v)} />
            <Bar dataKey="value" fill={CHART_COLORS[0]} radius={[4, 4, 0, 0]} activeBar={false}>
              {data.map((_: any, i: number) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
            </Bar>
          </BarChart>
        );
    }
  };

  return (
    <div className="my-3 p-3 bg-black/10 dark:bg-white/5 rounded-lg border border-white/10 overflow-hidden">
      {title && <h4 className="text-sm font-semibold mb-2 text-center">{title}</h4>}
      <ResponsiveContainer width="99%" height={chartHeight}>
        {renderChart()}
      </ResponsiveContainer>
    </div>
  );
}

export function parseContentWithCharts(content: string): Array<{ type: 'text' | 'chart'; content: string; chartData?: any }> {
  const parts: Array<{ type: 'text' | 'chart'; content: string; chartData?: any }> = [];
  const regex = /(?:```(?:json)?\s*)?\[CHART\]\s*([\s\S]*?)\s*\[\/CHART\](?:\s*```)?/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: 'text', content: content.slice(lastIndex, match.index) });
    }
    try {
      const chartData = JSON.parse(match[1].trim());
      parts.push({ type: 'chart', content: '', chartData });
    } catch {
      parts.push({ type: 'text', content: match[0] });
    }
    lastIndex = regex.lastIndex;
  }

  if (lastIndex < content.length) {
    parts.push({ type: 'text', content: content.slice(lastIndex) });
  }

  return parts.length > 0 ? parts : [{ type: 'text', content }];
}

/**
 * Renders a chat message with markdown, tables, and charts.
 * Dark theme variant for the Manager PA.
 */
export function FormattedChatMessage({ content, darkTheme = false }: { content: string; darkTheme?: boolean }) {
  const borderColor = darkTheme ? '#52525b' : '#d4d4d8';
  const thBg = darkTheme ? 'bg-zinc-800' : 'bg-zinc-100';
  const codeBg = darkTheme ? 'text-[#7c75f0] bg-[#5048e5]/10' : 'text-primary bg-primary/10';

  const components: Components = useMemo(() => ({
    table: ({ children, ...props }) => (
      <div className="my-2 overflow-x-auto max-w-full not-prose">
        <table {...props} className="text-xs w-full border-collapse min-w-0" style={{ border: `1px solid ${borderColor}` }}>{children}</table>
      </div>
    ),
    th: ({ children, ...props }) => (
      <th {...props} className={`text-left p-2 font-semibold ${thBg} text-xs`} style={{ border: `1px solid ${borderColor}` }}>{children}</th>
    ),
    td: ({ children, ...props }) => (
      <td {...props} className="p-2 text-xs align-top" style={{ border: `1px solid ${borderColor}` }}>{children}</td>
    ),
    pre: ({ children, ...props }) => (
      <pre {...props} className="my-2 p-3 bg-black/20 rounded-lg overflow-x-auto text-xs">{children}</pre>
    ),
    code: ({ children, className, ...props }) => {
      if (className?.includes('language-')) return <code className={className} {...props}>{children}</code>;
      return <code className={`${codeBg} px-1 py-0.5 rounded text-xs`} {...props}>{children}</code>;
    },
    strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
    a: ({ children, href }) => <a href={href} target="_blank" rel="noopener" className="text-[#7c75f0] hover:underline">{children}</a>,
  }), [borderColor, thBg, codeBg]);

  const safeContent = typeof content === 'string' ? content : JSON.stringify(content || '');
  const parts = useMemo(() => parseContentWithCharts(safeContent), [safeContent]);

  return (
    <div className="overflow-hidden max-w-full">
      {parts.map((part, i) =>
        part.type === 'chart' && part.chartData ? (
          <ChartRenderer key={i} chartData={part.chartData} />
        ) : (
          <div key={i} className="text-sm prose prose-sm prose-invert max-w-none prose-p:my-1.5 prose-ul:my-1 prose-ol:my-1 overflow-hidden break-words">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
              {part.content}
            </ReactMarkdown>
          </div>
        )
      )}
    </div>
  );
}
